import {useState} from "react";
import {toast} from "sonner";
import {Card} from "@/components/ui/card.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import {Spinner} from "@/components/ui/spinner.tsx";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import {
    ChevronUpIcon, ChevronDownIcon, PencilIcon, TrashIcon, PlusIcon,
} from "@heroicons/react/24/outline";
import {IVR_FIELD_TYPE_OPTIONS, IvrField} from "@/types/SClassTest.ts";
import {
    useGetIvrFieldsQuery, useDeleteIvrFieldMutation, useReorderIvrFieldsMutation,
} from "@/store/sclassTest.ts";
import {QuestionFormDialog} from "@/pages/sclass-test/components/QuestionFormDialog.tsx";
import {formatApiError} from "@/pages/sclass-test/apiError.ts";

function typeLabel(type: string) {
    return IVR_FIELD_TYPE_OPTIONS.find(o => o.value === type)?.label || type;
}

export function QuestionsList({sclassTestId}: { sclassTestId: number }) {
    const {data: questions, isLoading} = useGetIvrFieldsQuery(sclassTestId);
    const [deleteQuestion] = useDeleteIvrFieldMutation();
    const [reorderQuestions] = useReorderIvrFieldsMutation();

    const [editing, setEditing] = useState<IvrField | null | undefined>(undefined);
    const [formOpen, setFormOpen] = useState(false);
    const [toDelete, setToDelete] = useState<IvrField | null>(null);

    function openCreate() {
        setEditing(null);
        setFormOpen(true);
    }

    function openEdit(q: IvrField) {
        setEditing(q);
        setFormOpen(true);
    }

    async function handleDelete() {
        if (!toDelete) return;
        try {
            await deleteQuestion({id: toDelete.id, sclassTestId}).unwrap();
            toast.success('השאלה נמחקה בהצלחה');
        } catch (e: any) {
            toast.error('שגיאה במחיקת השאלה', {description: formatApiError(e)});
        } finally {
            setToDelete(null);
        }
    }

    async function move(index: number, direction: -1 | 1) {
        if (!questions) return;
        const target = index + direction;
        if (target < 0 || target >= questions.length) return;
        const a = questions[index];
        const b = questions[target];
        try {
            await reorderQuestions({
                sclassTestId,
                order: [
                    {id: a.id, order: b.order ?? target + 1},
                    {id: b.id, order: a.order ?? index + 1},
                ],
            }).unwrap();
        } catch (e: any) {
            toast.error('שגיאה בעדכון סדר השאלות', {description: formatApiError(e)});
        }
    }

    const nextOrder = questions && questions.length
        ? Math.max(...questions.map(q => q.order ?? 0)) + 1
        : 1;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-neutral-800">
                    שאלות {questions ? `(${questions.length})` : ''}
                </h2>
                <Button type="button" onClick={openCreate} className="gap-1">
                    <PlusIcon className="size-4"/>
                    הוספת שאלה
                </Button>
            </div>

            {isLoading && (
                <div className="flex justify-center py-10">
                    <Spinner/>
                </div>
            )}

            {!isLoading && questions?.length === 0 && (
                <Card className="p-8 text-center text-sm text-neutral-500">
                    עדיין לא נוספו שאלות למקבץ זה. לחץ על "הוספת שאלה" כדי להתחיל.
                </Card>
            )}

            <div className="space-y-2">
                {questions?.map((q, index) => (
                    <Card key={q.id} className="p-4 flex items-center gap-3">
                        <div className="flex flex-col">
                            <Button variant="ghost" size="icon-xs" disabled={index === 0}
                                    onClick={() => move(index, -1)}>
                                <ChevronUpIcon className="size-4"/>
                            </Button>
                            <Button variant="ghost" size="icon-xs" disabled={index === (questions.length - 1)}
                                    onClick={() => move(index, 1)}>
                                <ChevronDownIcon className="size-4"/>
                            </Button>
                        </div>

                        <div className="flex-grow min-w-0">
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="font-mono">#{q.order ?? '—'}</Badge>
                                <span className="font-medium text-neutral-800 truncate">{q.desc}</span>
                                <Badge variant="flat">{typeLabel(q.type)}</Badge>
                            </div>
                            {q.note && <div className="text-xs text-neutral-400 truncate">{q.note}</div>}
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                            <Button type="button" variant="outline" size="icon-sm" onClick={() => openEdit(q)}>
                                <PencilIcon className="size-4"/>
                            </Button>
                            <Button type="button" variant="outline" size="icon-sm"
                                    className="hover:bg-red-50 hover:text-red-600"
                                    onClick={() => setToDelete(q)}>
                                <TrashIcon className="size-4"/>
                            </Button>
                        </div>
                    </Card>
                ))}
            </div>

            <QuestionFormDialog
                open={formOpen}
                onOpenChange={setFormOpen}
                sclassTestId={sclassTestId}
                question={editing}
                nextOrder={nextOrder}
            />

            <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>מחיקת שאלה</AlertDialogTitle>
                        <AlertDialogDescription>
                            האם אתה בטוח שברצונך למחוק את השאלה "{toDelete?.desc}"? פעולה זו אינה הפיכה.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>ביטול</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                            מחיקה
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

